import { makeStyles } from '~/UIKit/Style';
import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LIGHTER_BLUE, VERTICAL_SEPARATOR, WHITE } from '~/Constants/Colors.constants';
import { FONT_SIZE_LARGE } from '~/Constants/UI.constants';
import { Typography, Checkbox, FormControlLabel } from '~/UIKit';
import {
  IItemCreationSubsectionInputProps,
  ItemCreationFieldValue,
  ItemCreationSubsection,
} from '~/Components';
import { ExtendedLocation } from '../../ExtendedLocation.model';
import { useCountries } from '../../useCountries';
import { MapThin, Person, HomeThin } from '~/Components/Icons';
import { ItemCreationValidInput } from '~/Components/ItemCreation/ItemCreationSubsection/ItemCreationSubsectionInput.model';

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: 25,
  },
  wrapper: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.down('md')]: {
      background: WHITE,
      flexDirection: 'column',
      alignItems: 'start',
      position: 'relative',
      borderBottom: 0,
    },
  },
  title: {
    fontWeight: 'bolder',
    fontSize: FONT_SIZE_LARGE,
    display: 'flex',
    alignItems: 'center',
    width: 'fit-content',

    [theme.breakpoints.down('md')]: {
      padding: `0 ${theme.spacing(2)}`,
    },
  },
  description: {
    marginTop: theme.spacing(1),
    gap: '35px',
    display: 'flex',

    [theme.breakpoints.down('md')]: {
      padding: `0 ${theme.spacing(2)}`,
    },
  },
  form: {
    width: '100%',

    [theme.breakpoints.down('md')]: {
      '> p': {
        marginBottom: theme.spacing(2),
      },
    },
  },
  itemContainer: {
    padding: 0,
    backgroundColor: WHITE,
    [theme.breakpoints.up('md')]: {
      backgroundColor: LIGHTER_BLUE,
      padding: theme.spacing(2),
    },
  },
  itemContainerHidden: {
    display: 'none',
  },
  customElementWrapper: {
    display: 'flex',
    alignItems: 'center',
    background: WHITE,
    minHeight: 50,
    justifyContent: 'space-between',
    padding: '0 7px 0 15px',
    borderBottom: `thin solid ${VERTICAL_SEPARATOR}`,
    position: 'relative',
    opacity: 1,
  },
}));

export const LocationEditCustomWelcomePack = ({
  location,
  setFieldValue,
}: {
  location: ExtendedLocation;
  setFieldValue: ItemCreationFieldValue;
}) => {
  const [useSameAddress, setUseSameAddress] = useState(false);

  const { classes } = useStyles();
  const { isValidAddress, countries } = useCountries(location);
  const { t } = useTranslation();

  const values = location?.welcomePackageSettings;
  const filedBaseName = 'welcomePackageSettings';
  const fields = {
    receiverName: `${filedBaseName}.receiverName`,
    companyName: `${filedBaseName}.companyName`,
    street: `${filedBaseName}.street`,
    zipCode: `${filedBaseName}.zipCode`,
    city: `${filedBaseName}.city`,
    country: `${filedBaseName}.country`,
  };

  const useSameAddressHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const EMPTY_FIELD = '';
    setUseSameAddress(event.target.checked);

    if (event.target.checked) {
      const { street, zipCode, city, countryISOA2 } = location;

      setFieldValue(fields.street, street);
      setFieldValue(fields.zipCode, zipCode);
      setFieldValue(fields.city, city);
      setFieldValue(fields.country, countryISOA2);
    } else {
      setFieldValue(fields.street, EMPTY_FIELD);
      setFieldValue(fields.zipCode, EMPTY_FIELD);
      setFieldValue(fields.city, EMPTY_FIELD);
      setFieldValue(fields.country, EMPTY_FIELD);
    }
  };

  const LocationGeneralDetails: IItemCreationSubsectionInputProps<Partial<any>>[] = [
    {
      hidden: !isValidAddress,
      customElement: (
        <div className={classes.customElementWrapper}>
          <FormControlLabel
            className={classes.checkbox}
            control={
              <Checkbox
                onChange={useSameAddressHandler}
                checked={useSameAddress}
                disabled={!isValidAddress}
              />
            }
            label={t('settings.title.qrCode.address.sameAddress')}
          />
        </div>
      ),
    },
    {
      icon: <Person />,
      label: `${t('settings.title.qrCode.welcomePack.field.receiverName')}*`,
      placeholder: t('settings.title.qrCode.welcomePack.field.receiverName'),
      type: ItemCreationValidInput.TEXT,
      name: fields.receiverName,
      hasAnError: values?.receiverName?.length === 0,
      noIconBackground: true,
    },
    {
      icon: <HomeThin />,
      label: t('settings.title.qrCode.welcomePack.field.companyName'),
      placeholder: t('app.optional'),
      type: ItemCreationValidInput.TEXT,
      name: fields.companyName,
      noIconBackground: true,
    },
    {
      icon: <MapThin />,
      label: `${t('form.field.location.street')}*`,
      placeholder: t('form.field.location.street'),
      type: ItemCreationValidInput.TEXT,
      name: fields.street,
      noIconBackground: true,
      hasAnError: !values?.street,
      disabled: useSameAddress,
    },
    {
      icon: <MapThin />,
      label: `${t('form.field.location.zip')}*`,
      placeholder: t('form.field.location.zip'),
      type: ItemCreationValidInput.TEXT,
      name: fields.zipCode,
      noIconBackground: true,
      hasAnError: !values?.zipCode,
      disabled: useSameAddress,
    },
    {
      icon: <MapThin />,
      label: `${t('form.field.location.city')}*`,
      placeholder: t('form.field.location.city'),
      type: ItemCreationValidInput.TEXT,
      name: fields.city,
      noIconBackground: true,
      hasAnError: !values?.city,
      disabled: useSameAddress,
    },
    {
      icon: <MapThin />,
      label: `${t('form.field.location.country')}*`,
      placeholder: t('form.field.location.country'),
      type: ItemCreationValidInput.SELECT,
      name: fields.country,
      hasAnError: !values?.country,
      disabled: useSameAddress,
      options: countries,
    },
  ];

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <Typography className={classes.title}>{t('settings.title.qrCode.welcomePack')}</Typography>
        <Typography className={classes.description}>
          {t('settings.title.qrCode.welcomePack.description')}
        </Typography>
      </div>
      <div className={classes.form}>
        <Typography className={classes.title}>
          {t('settings.title.qrCode.address.title')}
        </Typography>
        <ItemCreationSubsection items={LocationGeneralDetails} />
      </div>
    </div>
  );
};
